import type { TimeSeries } from '@piccolohealth/pbs-common';
import { ChakraV3, FloatingPopover } from '@piccolohealth/ui';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormSection } from '../../../../components/forms/FormSection';
import { HookedSubmitButton } from '../../../../components/forms/V3HookedSubmitButton';
import { zodResolver } from '../../../../utils/zod';
import type { UseTimeseriesChartReturn } from '../../../participantData/hooks/useTimeseriesChart';
import { timeSeriesSchema } from './TimeSeriesAddControl';
import { TimeSeriesButtonGroup } from './TimeSeriesButtonGroup';
import { TimeSeriesForm } from './TimeSeriesForm';

type FormValues = TimeSeries;

interface TimeSeriesEditFormProps {
  chart: UseTimeseriesChartReturn;
  series: TimeSeries;
  onClose: () => void;
}

export const TimeSeriesEditForm = (props: TimeSeriesEditFormProps) => {
  const { chart, series, onClose } = props;

  const methods = useForm<TimeSeries>({
    defaultValues: series,
    resolver: zodResolver(timeSeriesSchema),
  });

  const onSubmit = (values: FormValues) => {
    chart.onEditSeries(values.id, values);
    onClose();
  };

  return (
    <ChakraV3.Stack
      bg='white'
      gap={0}
      w='lg'
      layerStyle='outline.subtle'
      shadow='2xl'
      rounded='md'
      overflow='hidden'
      p={4}
    >
      <FormProvider {...methods}>
        <FormSection as='form' heading='Edit series' onSubmit={methods.handleSubmit(onSubmit)}>
          <ChakraV3.Separator />
          <TimeSeriesForm chart={chart} />
          <HookedSubmitButton w='full' size='sm'>
            Save
          </HookedSubmitButton>
        </FormSection>
      </FormProvider>
    </ChakraV3.Stack>
  );
};

interface Props {
  chart: UseTimeseriesChartReturn;
  series: TimeSeries;
}

export const TimeSeriesEditControl = (props: Props) => {
  const { series, chart } = props;

  const { open, onOpen, onClose } = ChakraV3.useDisclosure();

  return (
    <FloatingPopover
      open={open}
      setOpen={(v) => (v ? onOpen() : onClose())}
      render={() => <TimeSeriesEditForm chart={chart} series={series} onClose={onClose} />}
    >
      <TimeSeriesButtonGroup chart={chart} series={series} onClick={onOpen} />
    </FloatingPopover>
  );
};
